import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Button, Form, Spinner } from "react-bootstrap";

import {
  Card,
  CardGrid,
  GridForm,
  GridFormButtonContainer,
  GridFormItemWrapper,
} from "@bagel-web/components";

import AgentMomentNavigator from "../agent/AgentMomentNavigator";
import AgentMomentTimeline from "../agent/AgentMomentTimeline";
import AgentSelect from "../agent/AgentSelect";
import { agentQueries, sessionQueries } from "../queries";
import { CommonAPI, PlaygroundSource, PlaygroundSourcePrefill } from "../types";

/**
 * Step 1 — Source: load a source triple to seed from, or start empty on the
 * canned default configuration. The session id is free text loaded
 * explicitly; the agent is then picked from the loaded session, and the
 * moment is picked on Agent History's timeline and moment navigator,
 * defaulting to the agent's latest moment.
 */
const PlaygroundSourceStep = ({
  initialSource,
  commonAPI,
  onLoad,
  onStartEmpty,
}: {
  // A full source (seeded entry) or a partial prefill (URL params carrying
  // only a session, or a session + agent).
  initialSource: PlaygroundSourcePrefill | null;
  commonAPI: CommonAPI;
  onLoad: (source: PlaygroundSource) => void;
  onStartEmpty: () => void;
}) => {
  const [sessionId, setSessionId] = useState(initialSource?.sessionId ?? "");
  // The session whose agents and moments populate the pickers below — set by
  // the Load Session button (prefilled on a seeded or partially seeded
  // entry, which auto-loads the session's agents).
  const [loadedSessionId, setLoadedSessionId] = useState<string | null>(
    initialSource?.sessionId ?? null
  );
  const [agentId, setAgentId] = useState(initialSource?.agentId ?? "");
  const [momentId, setMomentId] = useState<number | null>(
    initialSource?.momentId ?? null
  );

  const {
    data: session,
    isFetching: sessionIsFetching,
    isError: sessionIsError,
    error: sessionError,
  } = useQuery({
    queryKey: sessionQueries.get(loadedSessionId ?? ""),
    queryFn: () => commonAPI.fetchSession(loadedSessionId!),
    enabled: loadedSessionId !== null,
  });
  const agents = session?.agents ?? [];
  const hasSelectedAgent = agents.some((agent) => agent.id === agentId);

  // Resolve the agent selection against each loaded session: a matching
  // selection is kept; a requested agent missing from the session clears the
  // field rather than guessing; no selection at all defaults to the
  // session's first agent.
  useEffect(() => {
    if (!session) {
      return;
    }
    const sessionAgents = session.agents ?? [];
    setAgentId((current) => {
      if (sessionAgents.some((agent) => agent.id === current)) {
        return current;
      }
      return current !== "" ? "" : (sessionAgents[0]?.id ?? "");
    });
  }, [session]);

  const {
    data: momentList,
    isPending: momentListPending,
    refetch: refetchMomentList,
  } = useQuery({
    queryKey: agentQueries.getMomentList(loadedSessionId ?? "", agentId),
    queryFn: () =>
      commonAPI.fetchAgentMomentList({
        sessionId: loadedSessionId!,
        agentId,
      }),
    enabled: loadedSessionId !== null && hasSelectedAgent,
  });

  // Settle the selection into each loaded moment list: a selection (or
  // prefill) present in the list is kept, anything else snaps to the
  // agent's latest moment.
  useEffect(() => {
    if (!momentList) {
      return;
    }
    const moments = momentList.moments;
    setMomentId((current) => {
      if (
        current !== null &&
        moments.some((moment) => moment.moment_id === current)
      ) {
        return current;
      }
      return moments[moments.length - 1]?.moment_id ?? null;
    });
  }, [momentList]);

  const trimmedSessionId = sessionId.trim();
  // The submitted triple must come from the loaded session — editing the
  // session field after a load disables submission until the new session is
  // loaded in turn.
  const isSessionLoaded = !!session && loadedSessionId === trimmedSessionId;
  // The selection resolved to its list entry. The list walks the clone
  // chain, so the entry — not the loaded session — names the moment's
  // owning session; seed lookups resolve (session, moment) against it.
  const selectedMoment =
    momentId !== null
      ? momentList?.moments.find((moment) => moment.moment_id === momentId)
      : undefined;
  const isLoadable = isSessionLoaded && hasSelectedAgent && !!selectedMoment;

  return (
    <>
      <CardGrid maxColumns={2} unitWidth={1600}>
        <Card colSpan={2} title="Experiment with Agents">
          <p>
            The Playground is a tool for safely experimenting with Agents.
            Choose an existing Agent to use as a starting point, or start with
            our Sample Agent.
          </p>
        </Card>
        <Card title="Start from Any Moment">
          <p className="text-body-secondary">
            Start from any point in an Agent's history and experiment on an
            alternate timeline.
          </p>
          <GridForm
            $width="100%"
            onSubmit={(event) => {
              event.preventDefault();
              if (isLoadable && selectedMoment) {
                onLoad({
                  sessionId: selectedMoment.session_id,
                  agentId,
                  momentId: selectedMoment.moment_id,
                });
              }
            }}
          >
            <GridFormItemWrapper colSpan={2}>
              <Form.Group controlId="playground-source-session">
                <Form.Label>Session ID</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    className="font-monospace"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={sessionId}
                    isInvalid={sessionIsError}
                    onChange={(e) => setSessionId(e.target.value)}
                  />
                  <Button
                    variant="secondary"
                    className="text-nowrap"
                    disabled={trimmedSessionId === "" || sessionIsFetching}
                    onClick={() => setLoadedSessionId(trimmedSessionId)}
                    data-testid="playground-load-session-button"
                  >
                    {sessionIsFetching ? (
                      <Spinner size="sm" />
                    ) : (
                      <>Load Session</>
                    )}
                  </Button>
                </div>
                {sessionIsError && (
                  <div
                    className="invalid-feedback d-block"
                    data-testid="playground-source-session-invalid"
                  >
                    Could not load session:{" "}
                    {sessionError instanceof Error
                      ? sessionError.message
                      : "unknown error"}
                  </div>
                )}
              </Form.Group>
            </GridFormItemWrapper>
            <GridFormItemWrapper>
              <Form.Label htmlFor="playground-source-agent">Agent</Form.Label>
              <AgentSelect
                inputId="playground-source-agent"
                agents={agents}
                value={agentId}
                placeholder={
                  !session
                    ? "Load a session first"
                    : agents.length === 0
                      ? "No agents in this session"
                      : "Select an agent"
                }
                isLoading={sessionIsFetching}
                isDisabled={!session || agents.length === 0}
                onChangeValue={setAgentId}
              />
            </GridFormItemWrapper>
            <GridFormItemWrapper>
              {/* Invisible label keeps the navigator's buttons level with
                  the Agent select beside it; the timestamp tucks under. */}
              <Form.Label className="d-block invisible">Moment</Form.Label>
              <AgentMomentNavigator
                momentId={momentId}
                momentList={momentList}
                momentListPending={momentListPending}
                setMomentId={(moment) => setMomentId(moment?.momentId ?? null)}
                refetchMomentList={refetchMomentList}
              />
            </GridFormItemWrapper>
            <GridFormItemWrapper colSpan={2}>
              <Form.Label>Moment</Form.Label>
              {loadedSessionId !== null && hasSelectedAgent && momentList ? (
                <AgentMomentTimeline
                  sessionId={loadedSessionId}
                  momentId={momentId}
                  momentList={momentList}
                  onSelectMoment={(moment) => setMomentId(moment.momentId)}
                />
              ) : (
                <p
                  className="text-body-secondary mb-0"
                  data-testid="playground-source-moment-placeholder"
                >
                  Load a session and choose an agent to pick a moment.
                </p>
              )}
            </GridFormItemWrapper>
            <GridFormButtonContainer>
              <Button
                type="submit"
                disabled={!isLoadable}
                data-testid="playground-load-button"
              >
                Start
              </Button>
            </GridFormButtonContainer>
          </GridForm>
        </Card>
        <Card title="Use a Sample Agent">
          <p className="text-body-secondary">
            Start with a Sample Agent. Tweak its role, components, functions and
            state, and see how it behaves.
          </p>
          <GridFormButtonContainer>
            <Button
              onClick={onStartEmpty}
              data-testid="playground-start-empty-button"
            >
              Start
            </Button>
          </GridFormButtonContainer>
        </Card>
      </CardGrid>
    </>
  );
};

export default PlaygroundSourceStep;
